import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, Chip, Button, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Collapse } from '@mui/material';
import { proposalService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import api from '../services/api';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FileUpload from '../components/FileUpload';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

export default function Proposals() {
  const { user } = useAuth();
  const [snack, setSnack] = React.useState(null);
  const handleDecline = async (proposalId) => {
    try {
      await api.put('/proposals/' + proposalId + '/decline');
      setProposals(prev => prev.map(p => p.id === proposalId ? { ...p, status: 'declined' } : p));
      setSnack({ type: 'success', msg: 'Proposal declined.' });
    } catch(e) {
      setSnack({ type: 'error', msg: e.response?.data?.error || 'Failed to decline proposal' });
    }
  };
  const [proposals, setProposals] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [reviseDialog, setReviseDialog] = useState({ open: false, proposal: null });
  const [reviseForm, setReviseForm] = useState({ proposed_amount: '', notes: '' });
  const [reviseFiles, setReviseFiles] = useState([]);
  const [revisions, setRevisions] = useState({});
  const [showRevisions, setShowRevisions] = useState({});

  useEffect(() => {
    proposalService.getMy().then(res => setProposals(res.data)).catch(() => {});
  }, []);

  const statusConfig = {
    submitted: { color: '#fff3e0', text: '#e65100' },
    under_review: { color: '#e3f2fd', text: '#1565c0' },
    accepted: { color: '#e8f5e9', text: '#2e7d32' },
    pending: { color: '#fff8e1', text: '#f57f17' },
    declined: { color: '#f3e5f5', text: '#7b1fa2' },
    rejected: { color: '#ffebee', text: '#c62828' },
    withdrawn: { color: '#f5f5f5', text: '#757575' },
  };

  const handleRevise = async () => {
    if (!reviseFiles || reviseFiles.length === 0) {
      alert('Please attach at least one proposal document.');
      return;
    }
    try {
      const payload = { ...reviseForm, attachments: reviseFiles };
      const r = await api.post(`/proposals/${reviseDialog.proposal.id}/revise`, payload);
      setProposals(ps => ps.map(p => p.id===reviseDialog.proposal.id ? r.data : p));
      setReviseDialog({ open: false, proposal: null });
      setReviseForm({ proposed_amount: '', notes: '' });
      setReviseFiles([]);
    } catch(e) { alert('Failed to submit revision'); }
  };

  const loadRevisions = async (id) => {
    if (revisions[id]) {
      setShowRevisions(s => ({...s, [id]: !s[id]}));
      return;
    }
    const r = await api.get(`/proposals/${id}/revisions`);
    setRevisions(s => ({...s, [id]: r.data}));
    setShowRevisions(s => ({...s, [id]: true}));
  };

  return (
    <Layout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e' }}>My Proposals</Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>{proposals.length} total proposals submitted</Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        {[
          { key: 'pending', label: 'Pending', color: '#f57f17', bg: '#fff8e1' },
          { key: 'accepted', label: 'Accepted', color: '#2e7d32', bg: '#e8f5e9' },
          { key: 'rejected', label: 'Not Selected', color: '#c62828', bg: '#ffebee' },
          { key: 'declined', label: 'Declined', color: '#7b1fa2', bg: '#f3e5f5' },
          { key: 'all', label: 'All', color: '#1a237e', bg: '#e8eaf6' },
        ].map(tab => {
          const count = tab.key === 'all' ? proposals.length : tab.key === 'pending' ? proposals.filter(p => ['submitted','pending','under_review'].includes(p.status)).length : proposals.filter(p => p.status === tab.key).length;
          return (
            <Box key={tab.key} onClick={() => setActiveTab(tab.key)}
              sx={{ cursor: 'pointer', px: 2, py: 0.75, borderRadius: 2, fontSize: 13, fontWeight: 700,
                bgcolor: activeTab === tab.key ? tab.color : tab.bg,
                color: activeTab === tab.key ? 'white' : tab.color,
                border: '1px solid ' + (activeTab === tab.key ? tab.color : 'transparent'),
                display: 'flex', alignItems: 'center', gap: 0.75, transition: 'all 0.15s' }}>
              {tab.label}
              <Box sx={{ bgcolor: activeTab === tab.key ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)',
                borderRadius: 10, px: 0.75, fontSize: 11, fontWeight: 800, minWidth: 18, textAlign: 'center' }}>
                {count}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Grid container spacing={3}>
        {proposals.filter(p => activeTab === 'all' || (activeTab === 'pending' && ['submitted','pending','under_review'].includes(p.status)) || p.status === activeTab).map(proposal => (
          <Grid item xs={12} md={6} lg={4} key={proposal.id}>
            <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, transition: 'all 0.2s', '&:hover': { boxShadow: '0 8px 24px rgba(26,35,126,0.12)' } }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#1a237e', borderRadius: 2 }}><AssignmentIcon /></Avatar>
                  <Chip label={proposal.status} size="small"
                    sx={{ bgcolor: statusConfig[proposal.status]?.color, color: statusConfig[proposal.status]?.text, fontWeight: 600 }} />
                </Box>
                {/* Address-first: address as primary title, project name as subtitle, scope as tag */}
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.25, color: '#1a237e', lineHeight: 1.2 }}>
                  {(proposal.address ? [proposal.address, proposal.city, proposal.state].filter(Boolean).join(', ') : null) || proposal.project_name || proposal.title}
                </Typography>
                {proposal.project_name && (proposal.address || proposal.city) && (
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', fontWeight: 600, mb: 0.25 }}>
                    {proposal.project_name}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5, mt: 0.5 }}>
                  {proposal.scope_title && <Chip label={proposal.scope_title} size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, height: 20, fontSize: 10 }} />}
                  {proposal.title && proposal.title !== proposal.scope_title && <Chip label={proposal.title} size="small" sx={{ bgcolor: '#e8eaf6', color: '#1a237e', fontWeight: 600, height: 20, fontSize: 10 }} />}
                </Box>
                <Typography variant="body2" sx={{ color: '#666', mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{proposal.description}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AttachMoneyIcon sx={{ fontSize: 18, color: '#43a047' }} />
                    <Typography variant="h6" sx={{ color: '#43a047', fontWeight: 700 }}>${Number(proposal.proposed_amount).toLocaleString()}</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#999' }}>{new Date(proposal.created_at).toLocaleDateString()}</Typography>
                </Box>
                {proposal.estimated_start_date && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f6fa', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      📅 {new Date(proposal.estimated_start_date).toLocaleDateString()} — {new Date(proposal.estimated_end_date).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  {proposal.status === 'rejected' && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: '#ffebee', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 600 }}>Another contractor was selected</Typography>
                    </Box>
                  )}
                  {proposal.status === 'declined' && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: '#f3e5f5', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#7b1fa2', fontWeight: 600 }}>You declined this scope</Typography>
                    </Box>
                  )}
                  {['submitted','under_review','pending'].includes(proposal.status) && (
                    <Button size="small" variant="outlined" fullWidth
                      onClick={() => handleDecline(proposal.id)}
                      sx={{ borderRadius: 2, mt: 0.5, borderColor: '#7b1fa2', color: '#7b1fa2',
                        '&:hover': { bgcolor: '#f3e5f5' }, fontSize: 11 }}>
                      Decline Scope
                    </Button>
                  )}
                  {['submitted','under_review'].includes(proposal.status) && (
                    <Button size="small" variant="outlined" fullWidth
                      onClick={() => { setReviseDialog({ open: true, proposal }); setReviseForm({ proposed_amount: proposal.proposed_amount, notes: '' }); }}
                      sx={{ borderColor: '#1a237e', color: '#1a237e', borderRadius: 2, fontSize: 12 }}>
                      ✏️ Revise
                    </Button>
                  )}
                  {proposal.revision_number > 1 && (
                    <Button size="small" variant="text" fullWidth onClick={() => loadRevisions(proposal.id)}
                      sx={{ color: '#666', fontSize: 12 }}>
                      📋 Rev {proposal.revision_number} · History
                    </Button>
                  )}
                </Box>
                <Collapse in={showRevisions[proposal.id]}>
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f5f6fa', borderRadius: 2 }}>
                    <Typography variant="caption" fontWeight={700} color="#1a237e">Revision History</Typography>
                    {(revisions[proposal.id]||[]).map(r => (
                      <Box key={r.id} sx={{ mt: 1, borderTop: '1px solid #e0e0e0', pt: 1 }}>
                        <Typography variant="caption" display="block" fontWeight={700}>Rev {r.revision_number} — ${Number(r.proposed_amount).toLocaleString()}</Typography>
                        {r.notes && <Typography variant="caption" color="text.secondary">{r.notes}</Typography>}
                        <Typography variant="caption" color="text.secondary" display="block">{new Date(r.created_at).toLocaleDateString()}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {proposals.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <AssignmentIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#999' }}>No proposals yet</Typography>
              <Typography variant="body2" sx={{ color: '#bbb' }}>Browse open scopes and submit your first proposal</Typography>
            </Box>
          </Grid>
        )}
      </Grid>
      {/* Revise Dialog */}
      <Dialog open={reviseDialog.open} onClose={() => setReviseDialog({ open: false, proposal: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', borderBottom: '1px solid #e0e0e0' }}>
          Submit Revision — Rev {(reviseDialog.proposal?.revision_number||1) + 1}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Revised Amount ($) *" type="number" value={reviseForm.proposed_amount}
              onChange={e => setReviseForm({...reviseForm, proposed_amount: e.target.value})} fullWidth />
            <TextField label="Revision Notes *" value={reviseForm.notes}
              onChange={e => setReviseForm({...reviseForm, notes: e.target.value})}
              fullWidth multiline rows={3} placeholder="Explain what changed and why..." />
            <Box>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1, fontWeight: 600 }}>
                Attach revised proposal documents *
              </Typography>
              <FileUpload onFilesChange={setReviseFiles} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => { setReviseDialog({ open: false, proposal: null }); setReviseFiles([]); }}>Cancel</Button>
          <Button variant="contained" onClick={handleRevise}
            disabled={!reviseForm.proposed_amount || !reviseForm.notes || !reviseFiles || reviseFiles.length === 0}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>Submit Revision</Button>
        </DialogActions>
      </Dialog>
      {snack && (
        <Box sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          bgcolor: snack.type === 'success' ? '#2e7d32' : '#c62828', color: 'white',
          px: 3, py: 1.5, borderRadius: 2, boxShadow: 3, zIndex: 9999,
          display: 'flex', alignItems: 'center', gap: 2, fontSize: 14 }}>
          {snack.msg}
          <Box onClick={() => setSnack(null)} sx={{ cursor: 'pointer', fontWeight: 700 }}>✕</Box>
        </Box>
      )}
    </Layout>
  );
}
